import { Resources } from 'constants/resources';

import React, { forwardRef, memo } from 'react';

import clsx from 'clsx';
import {
  DatagridCell,
  DatagridClasses,
  useGetMany,
  RecordContextProvider,
  useRecordContext,
  useResourceContext,
} from 'react-admin';
import { TableRow } from 'shared/mui/Table';
import { DatagridRowProps } from 'shared/react-admin/DatagridRows/DatagridRow/DatagridRow';
import { Commission } from 'types/embedded/commission';

type Props = Omit<DatagridRowProps, 'sx'>;

const CommissionsPureRow = forwardRef<HTMLTableRowElement, Props>((props, ref) => {
  const { children, className, hover = true, id, style, selectable = true, ...rest } = props;

  const record = useRecordContext(props);
  const resource = useResourceContext(props);

  const { data } = useGetMany<Commission>(
    Resources.Commissions,
    { ids: record?.children.map((c: Commission) => c?.['@id']) },
    { enabled: !!record?.children },
  );

  return (
    <RecordContextProvider value={record}>
      <TableRow
        className={clsx(className, {
          [DatagridClasses.selectable]: selectable,
        })}
        hover={hover}
        key={id}
        ref={ref}
        style={style}
        {...rest}
      >
        {React.Children.map(children, (field: any, index) => {
          if (!field?.props) {
            return <></>;
          }
          return (
            <DatagridCell
              className={clsx(`column-${field.props.source}`, DatagridClasses.rowCell)}
              key={index}
              record={record}
              {...{ field, resource }}
            />
          );
        })}
      </TableRow>
      {!!data?.length &&
        data.map((rec) => (
          <RecordContextProvider key={rec.id} value={rec}>
            <TableRow
              className={clsx(className)}
              hover={hover}
              key={id}
              ref={ref}
              {...rest}
              sx={{
                backgroundColor: 'tableHead',
              }}
            >
              {React.Children.map(children, (field: any, index) => {
                if (!field?.props) {
                  return <></>;
                }
                return (
                  <DatagridCell
                    className={clsx(`column-${field?.props?.source}`, DatagridClasses.rowCell)}
                    key={index}
                    record={rec}
                    {...{ field, resource }}
                  />
                );
              })}
            </TableRow>
          </RecordContextProvider>
        ))}
    </RecordContextProvider>
  );
});

export const DatagridCommissionsRow = memo(CommissionsPureRow);

CommissionsPureRow.displayName = 'CommissionsRow';
